import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import PreviewDashboard from '../shared/components/PreviewDashboard/index';
import MobileScreenTemplate from '../../Templates/MobileScreenTemplate';
import WidgetConfigurations from '../shared/components/WidgetConfigurations/index';
import useHomepageCard from '../shared/hooks/useHomepageCard';
import { useDashboardConfig } from '../../../queries/useDashboardConfig';
import { useTeamsHierarchy, teamsKey } from '../../../queries/useTeams';
import { teamsViewSortItems } from '../../../utils/casaUtils';
import { ROUTES_PATH } from '../../../constants/globalConstant';
import { usePreferences } from '../../../hooks/usePreferences';
import {
  getAllTeamsViewData,
  hierarchyToLinear,
  homepagesbyTeamIdMapping,
  menubyTeamIdMapping,
  announcementsbyTeamIdMapping
} from '../../../utils/globalUtils';
import TableListingShimmer from '../../../components/reusableComponents/listing/TableListing/Shimmer';
// Widget context not needed - tab resets when HomeScreen remounts
import { teamsViewSteps } from '../../../components/Coach/coachSteps';
import { useMenusData, useAnnouncementsData } from './hooks';
import { useTeamsFilters } from '../shared/hooks';
import { TeamsPageLayout, TeamsHeader, TeamsTableWrapper } from '../shared/components';
import { buildMobileTeamsColumnDefs } from './config/columnDefs';
import { MobileTeamsDataContext, useMobileTeamsData } from './MobileTeamsDataContext';
import { useMobileTeamsHomepages } from './hooks/useMobileTeamsHomepages';
import styles from './styles.module.css';

const TeamsContent = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const navigateTo = searchParams.get('navigateTo');

  // State
  const teamsViewSortState = useState({
    sortColumn: teamsViewSortItems[0].id,
    sortOrder: 1,
    label: teamsViewSortItems[0].label
  });
  const searchState = useState('');
  const { createHomepageClicked } = useHomepageCard();
  const [previewPanel, setPreviewPanel] = useState({
    state: false,
    data: null
  });
  const [dialog, setDialog] = useState({ state: false, dialogBox: {} });

  // Data fetching
  const { homepagesById, homepages: teamDashboards } = useMobileTeamsData();
  const teamsHierarchy = useTeamsHierarchy();
  const { mutate } = useSWRConfig();
  const { menuById, menus, loading: isMenuBuilderLoading } = useMenusData();
  const {
    announcementById,
    announcements,
    loading: isAnnouncementLoading
  } = useAnnouncementsData();

  const loading = isMenuBuilderLoading || isAnnouncementLoading;
  const { preferences, updatePreference } = usePreferences();
  const { data: appConfig } = useDashboardConfig();
  const teamsCoachMarksVisited = preferences?.teamsCoachMarksVisited;
  const sideNavigationCoachMarksVisited = preferences?.sideNavigationCoachMarksVisited;

  // Data transformation
  const menuByTeamId = menubyTeamIdMapping(menus?.teamMenu || []);
  const announcementByTeamId = announcementsbyTeamIdMapping(announcements?.teamAnnouncements || []);
  const teamsDashboards = teamDashboards?.teamsDashboards || [];
  const defaultTeamDashboards = teamDashboards?.defaultDashboards || [];
  const defaultMenu = menus?.defaultMenu || [];

  const linearTeams = useMemo(() => {
    return hierarchyToLinear(teamsHierarchy);
  }, [teamsHierarchy]);

  const teamsMappedWithTeamId = useMemo(() => {
    return Object.fromEntries(linearTeams.map((team) => [team.teamId, team]));
  }, [linearTeams]);

  const homepagesbyTeamId = homepagesbyTeamIdMapping(teamsDashboards);
  const defaultAndTeamsData = getAllTeamsViewData(
    linearTeams,
    homepagesbyTeamId,
    menuByTeamId,
    defaultTeamDashboards,
    defaultMenu,
    announcementByTeamId,
    announcements?.defaultAnnouncement
  );

  // Platform filter
  const platformFilterItems = useMemo(
    () => [
      { value: 'mobile', label: 'Mobile' },
      { value: 'web', label: 'Web' }
    ],
    []
  );

  const selectedPlatform =
    location.pathname === ROUTES_PATH.webTeams
      ? { value: 'web', label: 'Web' }
      : { value: 'mobile', label: 'Mobile' };

  const onPlatformSelect = (option) => {
    if (option?.value === 'web') {
      navigate(ROUTES_PATH.webTeams);
    } else {
      navigate(ROUTES_PATH.teams);
    }
  };

  // Filter and sort teams
  const filterAndSortedTeams = useTeamsFilters(
    defaultAndTeamsData.teamsData,
    defaultAndTeamsData.defaultTeam,
    searchState[0],
    teamsViewSortState[0].label,
    teamsMappedWithTeamId
  );

  // Column definitions
  const columnDefs = useMemo(
    () =>
      buildMobileTeamsColumnDefs({
        homepagesById,
        menuById,
        announcementById,
        createHomepageClicked,
        setPreviewPanel,
        setDialog
      }),
    [homepagesById, menuById, announcementById, createHomepageClicked, setPreviewPanel, setDialog]
  );

  useEffect(() => {
    mutate(teamsKey);
    // Tab resets automatically when HomeScreen remounts
    if (navigateTo === 'menu') {
      navigate(ROUTES_PATH.menubuilder);
    }
  }, []);

  const previewEditHandler = () => {
    const homepageId = previewPanel.data?.homepage?.homepageId;
    navigate(`${ROUTES_PATH.homebuilder}/${homepageId}`);
  };

  const onCoachMarkComplete = () => {
    updatePreference({ teamsCoachMarksVisited: true });
  };

  // Modals configuration
  const modals = [
    {
      state: previewPanel.state,
      backgroundStyle: {
        background: 'rgba(var(--ng-neutral-90), 0.5)',
        backdropFilter: 'blur(2px)'
      },
      content: (
        <PreviewDashboard
          key={previewPanel.data?.homepage?.homepageId}
          title={previewPanel.data?.title}
          editHandler={previewEditHandler}
          dismissHandler={() => setPreviewPanel({ state: false, data: null })}
          children1={
            <MobileScreenTemplate
              sections={previewPanel.data?.sections}
              isSectionsLoading={previewPanel.loading}
            />
          }
          children2={<WidgetConfigurations configuredData={previewPanel.data?.sections} />}
        />
      )
    },
    {
      state: dialog.state,
      backgroundStyle: {
        background: 'rgba(var(--ng-neutral-90), 0.5)',
        backdropFilter: 'blur(22px)'
      },
      content: dialog.dialogBox
    }
  ];

  return (
    <TeamsPageLayout
      loading={loading}
      showCoachMarks={sideNavigationCoachMarksVisited && !loading}
      coachMarksVisited={teamsCoachMarksVisited}
      coachMarksSteps={teamsViewSteps}
      onCoachMarkComplete={onCoachMarkComplete}
      modals={modals}
      dataTestId="teams-data-view">
      {loading ? (
        <TableListingShimmer columns={4} />
      ) : (
        <>
          <TeamsHeader
            searchState={searchState}
            sortState={teamsViewSortState}
            sortItems={teamsViewSortItems}
            platformFilterItems={
              appConfig?.casaWebEnabled && appConfig?.mobileHomeBuilderEnabled
                ? platformFilterItems
                : []
            }
            selectedPlatform={selectedPlatform}
            onPlatformSelect={onPlatformSelect}
          />

          <div className={`flex w-full h-full flex-column overflow-hidden`}>
            <div className={styles['loading-spacer']} />

            <TeamsTableWrapper
              data={filterAndSortedTeams}
              columnDefs={columnDefs}
              loading={loading}
              gridKey="mobile-teams-table"
              dataTestId="mobile-teams-table-grid"
            />
          </div>
        </>
      )}
    </TeamsPageLayout>
  );
};

const Teams = () => {
  const { homepagesById, homepages, refetchHomepages, refetchTeamDashboards } =
    useMobileTeamsHomepages();

  return (
    <MobileTeamsDataContext.Provider
      value={{
        homepagesById,
        homepages,
        refetchHomepages,
        refetchTeamsV2: refetchTeamDashboards
      }}>
      <TeamsContent />
    </MobileTeamsDataContext.Provider>
  );
};

export default Teams;
